import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiSave, FiSend, FiPlus, FiTrash2, FiChevronUp,
  FiChevronDown, FiImage, FiTag, FiType, FiEye, FiVideo, FiCode,
  FiAlertCircle, FiMessageSquare, FiMinus, FiLink, FiUpload,
  FiBold, FiItalic, FiList, FiBookOpen, FiExternalLink, FiZap,
  FiBarChart2, FiEdit3, FiActivity,
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import GoogleSignInGate from '../../components/GoogleSignInGate';
import UserBadge from '../../components/UserBadge';
import { BLOG_CATEGORIES, BLOG_TAGS } from '../../data/blogStore';
import { apiGetPost, apiCreatePost, apiUpdatePost, apiUploadFile } from '../../api/blogApi';

/* ═══════════════════════════════════════════════════════════════
   SECTION TYPE DEFINITIONS
   ═══════════════════════════════════════════════════════════════ */
const SECTION_TYPES = [
  { type: 'text',     label: 'Text',      icon: <FiType />,           desc: 'Rich paragraph text' },
  { type: 'image',    label: 'Image',     icon: <FiImage />,          desc: 'Image with caption' },
  { type: 'chart',    label: 'Chart',     icon: <FiBarChart2 />,      desc: 'Interactive chart / graph' },
  { type: 'video',    label: 'Video',     icon: <FiVideo />,          desc: 'YouTube / Vimeo embed' },
  { type: 'quote',    label: 'Quote',     icon: <FiMessageSquare />,  desc: 'Block quote with attribution' },
  { type: 'code',     label: 'Code',      icon: <FiCode />,           desc: 'Code block with language' },
  { type: 'callout',  label: 'Callout',   icon: <FiAlertCircle />,    desc: 'Info / Warning / Tip box' },
  { type: 'citation', label: 'Citation',  icon: <FiBookOpen />,       desc: 'Cite an external source' },
  { type: 'divider',  label: 'Divider',   icon: <FiMinus />,          desc: 'Visual section break' },
];

const CHART_PALETTE = [
  '#0052cc', '#7c3aed', '#059669', '#f59e0b', '#ef4444',
  '#06b6d4', '#ec4899', '#8b5cf6', '#f97316', '#14b8a6',
];

const CHART_PRESETS = [
  { name: 'Default', colors: ['#0052cc', '#7c3aed', '#059669', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#8b5cf6', '#f97316', '#14b8a6'] },
  { name: 'Ocean', colors: ['#0077b6', '#00b4d8', '#90e0ef', '#03045e', '#023e8a', '#0096c7', '#48cae4', '#ade8f4', '#caf0f8', '#002855'] },
  { name: 'Sunset', colors: ['#ff6b6b', '#ee5a24', '#f0932b', '#ffbe76', '#ffeaa7', '#fd79a8', '#e17055', '#fab1a0', '#fdcb6e', '#e55039'] },
  { name: 'Forest', colors: ['#2d6a4f', '#40916c', '#52b788', '#74c69d', '#95d5b2', '#1b4332', '#b7e4c7', '#d8f3dc', '#081c15', '#344e41'] },
  { name: 'Neon', colors: ['#00f5d4', '#00bbf9', '#fee440', '#f15bb5', '#9b5de5', '#00c49a', '#fb5607', '#ff006e', '#3a86ff', '#8338ec'] },
  { name: 'Grayscale', colors: ['#1e293b', '#334155', '#475569', '#64748b', '#94a3b8', '#cbd5e1', '#e2e8f0', '#f1f5f9', '#0f172a', '#f8fafc'] },
];

const CODE_LANGUAGES = [
  'javascript', 'typescript', 'python', 'bash', 'sql', 'json', 'html', 'css',
  'java', 'go', 'rust', 'c', 'cpp', 'ruby', 'php', 'yaml', 'markdown', 'other',
];

const CALLOUT_VARIANTS = [
  { value: 'info',    label: 'Info',    icon: '💡' },
  { value: 'warning', label: 'Warning', icon: '⚠️' },
  { value: 'tip',     label: 'Tip',     icon: '🚀' },
  { value: 'note',    label: 'Note',    icon: '📝' },
];

/* Helper: parse YouTube/Vimeo URL -> embed URL */
function getVideoEmbedUrl(url) {
  if (!url) return null;
  // YouTube
  let m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  // Vimeo
  m = url.match(/vimeo\.com\/(\d+)/);
  if (m) return `https://player.vimeo.com/video/${m[1]}`;
  return null;
}

/* Helper: resolve uploaded file URL */
function resolveUploadUrl(url) {
  if (!url) return '';
  return url.startsWith('http') ? url : `http://localhost:3001/static/${url.replace(/^uploads\//, '')}`;
}

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */
export default function BlogEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = Boolean(id);

  const [tab, setTab] = useState('details');
  const [showBlockMenu, setShowBlockMenu] = useState(false);
  const [form, setForm] = useState({
    title: '',
    author: '',
    authorRole: '',
    category: BLOG_CATEGORIES[0],
    tags: [],
    excerpt: '',
    coverImage: '',
    readTime: 5,
    status: 'draft',
    featured: false,
    sections: [],
  });

  const [tagInput, setTagInput] = useState('');
  const [coverUploading, setCoverUploading] = useState(false);
  const [sectionUploading, setSectionUploading] = useState(null); // idx of section uploading an image

  useEffect(() => {
    if (isEdit) {
      apiGetPost(id).then((res) => {
        if (res.ok && res.data) {
          const post = res.data;
          setForm({
            title: post.title || '',
            author: post.author || '',
            authorRole: post.authorRole || '',
            category: post.category || BLOG_CATEGORIES[0],
            tags: post.tags || [],
            excerpt: post.excerpt || '',
            coverImage: post.coverImage || '',
            readTime: post.readTime || 5,
            status: post.status || 'draft',
            featured: post.featured || false,
            sections: post.sections || [],
          });
        }
      });
    } else if (user) {
      setForm((f) => ({ ...f, author: user.displayName || '' }));
    }
  }, [id, isEdit, user]);

  /* ─── Upload handlers ─────────────────────────────────────── */
  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const result = await apiUploadFile(file);
      if (result.ok) {
        setForm((f) => ({ ...f, coverImage: resolveUploadUrl(result.data.url) }));
      }
    } catch (err) { console.error('Cover upload failed:', err); }
    setCoverUploading(false);
  };

  const handleSectionImageUpload = async (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSectionUploading(idx);
    try {
      const result = await apiUploadFile(file);
      if (result.ok) {
        updateSection(idx, { url: resolveUploadUrl(result.data.url) });
      }
    } catch (err) { console.error('Image upload failed:', err); }
    setSectionUploading(null);
  };

  /* ─── Form helpers ────────────────────────────────────────── */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (t && !form.tags.includes(t)) setForm({ ...form, tags: [...form.tags, t] });
    setTagInput('');
  };

  const removeTag = (t) => setForm({ ...form, tags: form.tags.filter((x) => x !== t) });

  /* ─── Section management ──────────────────────────────────── */
  const defaultSectionData = (type) => {
    switch (type) {
      case 'text':     return { type, title: '', content: '' };
      case 'image':    return { type, url: '', caption: '', alt: '' };
      case 'video':    return { type, url: '', caption: '' };
      case 'quote':    return { type, content: '', attribution: '', source: '' };
      case 'code':     return { type, language: 'javascript', content: '', caption: '' };
      case 'callout':  return { type, variant: 'info', title: '', content: '' };
      case 'citation': return { type, title: '', author: '', publication: '', url: '', date: '', excerpt: '' };
      case 'divider':  return { type, style: 'line' };
      default:         return { type: 'text', title: '', content: '' };
    }
  };

  const addSection = (type = 'text') => {
    setForm({ ...form, sections: [...form.sections, defaultSectionData(type)] });
    setShowBlockMenu(false);
    setTab('content');
  };

  const updateSection = (idx, updates) => {
    const sections = [...form.sections];
    sections[idx] = { ...sections[idx], ...updates };
    setForm({ ...form, sections });
  };

  const duplicateSection = (idx) => {
    const sections = [...form.sections];
    sections.splice(idx + 1, 0, { ...sections[idx] });
    setForm({ ...form, sections });
  };

  const deleteSection = (idx) => {
    setForm({ ...form, sections: form.sections.filter((_, i) => i !== idx) });
  };

  const moveSection = (idx, dir) => {
    const sections = [...form.sections];
    const target = idx + dir;
    if (target < 0 || target >= sections.length) return;
    [sections[idx], sections[target]] = [sections[target], sections[idx]];
    setForm({ ...form, sections });
  };

  /* ─── Text formatting toolbar ─────────────────────────────── */
  const insertMarkdown = (idx, prefix, suffix = prefix) => {
    const ta = document.querySelector(`#section-body-${idx}`);
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = form.sections[idx].content;
    const selected = text.substring(start, end);
    const newText = text.substring(0, start) + prefix + selected + suffix + text.substring(end);
    updateSection(idx, { content: newText });
    // Restore cursor after React re-render
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + prefix.length, end + prefix.length);
    }, 0);
  };

  /* ─── Save ────────────────────────────────────────────────── */
  const handleSave = async (status) => {
    const data = { ...form, status };
    try {
      if (isEdit) await apiUpdatePost(id, data);
      else await apiCreatePost(data);
    } catch (err) { console.error('Save failed:', err); }
    navigate('/admin/blog');
  };

  /* ─── Word count ──────────────────────────────────────────── */
  const wordCount = form.sections.reduce((total, s) => {
    const text = s.content || s.excerpt || '';
    return total + text.split(/\s+/).filter(Boolean).length;
  }, 0);

  /* ─── Test Autofill ───────────────────────────────────────── */
  const handleTestAutofill = () => {
    setForm({
      title: 'Building Autonomous AI Agents: A Practical Guide',
      author: user?.displayName || 'Dr. Sarah Chen',
      authorRole: 'Lead AI Engineer',
      category: 'AI & Automation',
      tags: ['ai', 'automation', 'machine learning', 'llm', 'enterprise'],
      excerpt: 'A deep dive into how we architected self-healing AI agents that replaced 200+ manual workflows at a Fortune 500 logistics company — from prompt design to production deployment.',
      coverImage: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80',
      readTime: 12,
      status: 'draft',
      featured: true,
      sections: [
        {
          type: 'text',
          title: 'Introduction',
          content: 'Autonomous AI agents represent a **paradigm shift** in how enterprises handle complex, multi-step workflows. Unlike traditional rule-based automation, these agents can *reason*, *adapt*, and *self-correct* in real time.\n\nIn this article, we walk through the architecture behind our agent framework — covering everything from [LangChain](https://langchain.com) orchestration to custom tool integrations.\n\n• Reduced manual intervention by 94%\n• Cut processing time from 3 hours to 8 minutes\n• Self-healing error recovery with <2% failure rate',
        },
        {
          type: 'chart',
          title: 'Performance Impact',
          caption: 'Weekly task completion rates before and after deploying autonomous agents across 4 departments.',
          chartConfig: {
            chartType: 'grouped-bar',
            title: 'Tasks Completed Per Week by Department',
            height: 320,
            data: [
              { name: 'Logistics', before: 120, after: 340 },
              { name: 'Finance', before: 85, after: 210 },
              { name: 'HR', before: 65, after: 180 },
              { name: 'Engineering', before: 200, after: 420 },
            ],
            series: [
              { dataKey: 'before', label: 'Before Agents', color: '#ef4444' },
              { dataKey: 'after', label: 'After Agents', color: '#059669' },
            ],
          },
        },
        {
          type: 'image',
          url: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=800&q=80',
          caption: 'Our agent orchestration architecture handles 50k+ tasks daily',
          alt: 'AI agent architecture diagram showing orchestration flow',
        },
        {
          type: 'quote',
          content: 'The best AI systems don\'t replace humans — they amplify human judgment by handling the 80% of work that\'s repetitive, so people can focus on the 20% that requires creativity.',
          attribution: 'Dr. Sarah Chen',
          source: 'Vector Labs Engineering Blog',
        },
        {
          type: 'chart',
          title: 'Error Rate Over Time',
          caption: 'Self-healing agents continuously improve. Error rates dropped below 1% within 3 weeks of deployment.',
          chartConfig: {
            chartType: 'area',
            title: 'Agent Error Rate (%) — 8-Week Rollout',
            height: 280,
            data: [
              { name: 'Week 1', errorRate: 8.4, manualFixes: 47 },
              { name: 'Week 2', errorRate: 5.2, manualFixes: 28 },
              { name: 'Week 3', errorRate: 2.8, manualFixes: 14 },
              { name: 'Week 4', errorRate: 1.6, manualFixes: 8 },
              { name: 'Week 5', errorRate: 1.1, manualFixes: 5 },
              { name: 'Week 6', errorRate: 0.8, manualFixes: 3 },
              { name: 'Week 7', errorRate: 0.6, manualFixes: 2 },
              { name: 'Week 8', errorRate: 0.4, manualFixes: 1 },
            ],
            series: [
              { dataKey: 'errorRate', label: 'Error Rate %', color: '#ef4444' },
              { dataKey: 'manualFixes', label: 'Manual Fixes/Week', color: '#f59e0b' },
            ],
          },
        },
        {
          type: 'code',
          language: 'python',
          caption: 'agent_orchestrator.py — Core agent loop',
          content: 'from langchain.agents import AgentExecutor\nfrom langchain.tools import Tool\n\nclass AutonomousAgent:\n    def __init__(self, llm, tools, memory):\n        self.executor = AgentExecutor(\n            agent=llm,\n            tools=tools,\n            memory=memory,\n            max_iterations=10,\n            early_stopping_method="generate"\n        )\n\n    async def run(self, task: str) -> dict:\n        try:\n            result = await self.executor.arun(task)\n            return {"status": "success", "output": result}\n        except Exception as e:\n            return await self.self_heal(task, e)',
        },
        {
          type: 'chart',
          title: 'Cost Savings Breakdown',
          caption: 'Monthly cost comparison across infrastructure, API, and personnel — 73% total savings.',
          chartConfig: {
            chartType: 'stacked-bar',
            title: 'Monthly Cost Breakdown ($)',
            height: 300,
            data: [
              { name: 'Before', infrastructure: 12000, apiCalls: 0, personnel: 28000 },
              { name: 'After', infrastructure: 4200, apiCalls: 3800, personnel: 2800 },
            ],
            series: [
              { dataKey: 'infrastructure', label: 'Infrastructure', color: '#0052cc' },
              { dataKey: 'apiCalls', label: 'API Calls', color: '#7c3aed' },
              { dataKey: 'personnel', label: 'Personnel', color: '#f59e0b' },
            ],
          },
        },
        {
          type: 'callout',
          variant: 'tip',
          title: 'Pro Tip: Error Budget Strategy',
          content: 'Set a 5% error budget for your agents. If self-healing recovery exceeds this threshold in a rolling 24h window, automatically fall back to human-in-the-loop mode. This prevents cascading failures while maintaining trust.',
        },
        {
          type: 'chart',
          title: 'Agent Capability Profile',
          caption: 'Radar view of our agent capabilities across 6 key dimensions, benchmarked against industry standards.',
          chartConfig: {
            chartType: 'radar',
            title: 'Capability Benchmark',
            height: 340,
            data: [
              { name: 'Reasoning', ours: 94, industry: 72 },
              { name: 'Speed', ours: 88, industry: 81 },
              { name: 'Accuracy', ours: 97, industry: 76 },
              { name: 'Cost Eff.', ours: 91, industry: 58 },
              { name: 'Reliability', ours: 99, industry: 84 },
              { name: 'Self-Heal', ours: 96, industry: 42 },
            ],
            series: [
              { dataKey: 'ours', label: 'Our Framework', color: '#0052cc' },
              { dataKey: 'industry', label: 'Industry Avg', color: '#94a3b8' },
            ],
          },
        },
        {
          type: 'callout',
          variant: 'warning',
          title: 'Cost Considerations',
          content: 'GPT-4 API calls at scale can cost $2,000+/day. We reduced costs by 73% using a tiered model strategy: GPT-4 for complex reasoning, GPT-3.5-turbo for classification, and local Llama models for structured extraction.',
        },
        {
          type: 'citation',
          title: 'ReAct: Synergizing Reasoning and Acting in Language Models',
          author: 'Shunyu Yao, Jeffrey Zhao, Dian Yu, et al.',
          publication: 'ICLR 2023',
          url: 'https://arxiv.org/abs/2210.03629',
          date: '2023-01-01',
          excerpt: 'ReAct prompts LLMs to generate both reasoning traces and task-specific actions in an interleaved manner, allowing for greater synergy between the two.',
        },
        {
          type: 'divider',
          style: 'stars',
        },
        {
          type: 'text',
          title: 'Conclusion & Next Steps',
          content: 'Autonomous agents are no longer science fiction — they\'re **production-ready** tools that deliver measurable ROI.\n\nOur roadmap for Q2 2026 includes:\n\n1. Multi-agent collaboration protocols\n2. Real-time cost optimization via dynamic model routing\n3. Enterprise SSO integration for agent access control\n4. Visual agent builder for non-technical stakeholders\n\n> The future of enterprise software isn\'t more dashboards — it\'s agents that act on your behalf.\n\nReach out to [our consulting team](https://vectorlabsnc.com/consulting) to learn how autonomous agents can transform your operations.',
        },
      ],
    });
    setTab('content');
  };

  /* ═══════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════ */
  return (
    <GoogleSignInGate>
    <div className="admin-page">
      {/* Shared admin header */}
      <div className="admin-header">
        <div className="admin-header-inner">
          <Link to="/" className="research-brand">
            <span className="research-brand-text"><strong>Vector Labs</strong> Admin</span>
          </Link>
          <nav className="admin-nav">
            <Link to="/admin/writing" className="admin-nav-link"><FiEdit3 style={{marginRight:4}} />Writing</Link>
            <Link to="/admin/usage" className="admin-nav-link"><FiActivity style={{marginRight:4}} />Usage</Link>
            <Link to="/" className="admin-nav-link">Home</Link>
          </nav>
          <UserBadge />
        </div>
      </div>

      <div className="admin-body">
        <Link to="/admin/blog" className="rd-back" style={{ marginBottom: 24, display: 'inline-flex' }}>
          <FiArrowLeft /> Back to Blog Admin
        </Link>

        <div className="editor-card">
          {/* Top bar with title + save buttons */}
          <div className="ve-topbar">
            <h1 className="editor-title">{isEdit ? 'Edit Article' : 'New Article'}</h1>
            <div className="ve-topbar-meta">
              <span className="ve-word-count">{wordCount} words</span>
              <span className="ve-section-count">{form.sections.length} blocks</span>
            </div>
            <div className="ve-topbar-actions">
              {!isEdit && (
                <button className="admin-btn" onClick={handleTestAutofill}
                  style={{ background: '#7c3aed', color: '#fff', border: 'none' }}>
                  <FiZap /> Test Fill
                </button>
              )}
              <button className="admin-btn secondary" onClick={() => handleSave('draft')}>
                <FiSave /> Save Draft
              </button>
              <button className="admin-btn primary" onClick={() => handleSave('published')}>
                <FiSend /> Publish
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="editor-tabs">
            <button className={`editor-tab ${tab === 'details' ? 'active' : ''}`} onClick={() => setTab('details')}>
              Details
            </button>
            <button className={`editor-tab ${tab === 'content' ? 'active' : ''}`} onClick={() => setTab('content')}>
              Content ({form.sections.length})
            </button>
            <button className={`editor-tab ${tab === 'preview' ? 'active' : ''}`} onClick={() => setTab('preview')}>
              <FiEye /> Preview
            </button>
          </div>

          {/* ════════════════════════════════════════════════════
             DETAILS TAB
             ════════════════════════════════════════════════════ */}
          {tab === 'details' && (
            <div className="blog-editor-form">
              <div className="blog-editor-field full">
                <label>Title</label>
                <input name="title" value={form.title} onChange={handleChange}
                  placeholder="Article title..." className="blog-editor-title-input" />
              </div>
              <div className="blog-editor-row">
                <div className="blog-editor-field">
                  <label>Author</label>
                  <input name="author" value={form.author} onChange={handleChange} placeholder="Author name" />
                </div>
                <div className="blog-editor-field">
                  <label>Author Role</label>
                  <input name="authorRole" value={form.authorRole} onChange={handleChange} placeholder="e.g. Founder & CEO" />
                </div>
              </div>
              <div className="blog-editor-row">
                <div className="blog-editor-field">
                  <label>Category</label>
                  <select name="category" value={form.category} onChange={handleChange}>
                    {BLOG_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="blog-editor-field">
                  <label>Read Time (min)</label>
                  <input name="readTime" type="number" value={form.readTime} onChange={handleChange} min="1" max="60" />
                </div>
              </div>
              <div className="blog-editor-field full">
                <label>Excerpt / Summary</label>
                <textarea name="excerpt" value={form.excerpt} onChange={handleChange}
                  placeholder="A short summary for listings and SEO..." rows={3} />
              </div>
              <div className="blog-editor-field full">
                <label><FiImage /> Cover Image</label>
                <div className="blog-editor-cover-row">
                  <input name="coverImage" value={form.coverImage} onChange={handleChange}
                    placeholder="https://images.unsplash.com/... or upload" style={{ flex: 1 }} />
                  <label className="admin-btn secondary blog-editor-upload-btn" style={{ cursor: 'pointer', margin: 0 }}>
                    {coverUploading ? 'Uploading...' : <><FiUpload /> Upload</>}
                    <input type="file" accept="image/*" onChange={handleCoverUpload}
                      style={{ display: 'none' }} disabled={coverUploading} />
                  </label>
                </div>
                {form.coverImage && (
                  <div className="blog-editor-cover-preview">
                    <img src={form.coverImage} alt="Cover preview" />
                  </div>
                )}
              </div>
              <div className="blog-editor-field full">
                <label><FiTag /> Tags</label>
                <div className="blog-editor-tag-input">
                  <input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                    placeholder="Type a tag and press Enter" />
                  <button onClick={addTag} type="button">Add</button>
                </div>
                <div className="blog-editor-tags">
                  {form.tags.map((t) => (
                    <span key={t} className="blog-editor-tag-chip">
                      {t} <button onClick={() => removeTag(t)}>&times;</button>
                    </span>
                  ))}
                </div>
                <div className="blog-editor-suggested-tags">
                  <small>Suggested:</small>
                  {BLOG_TAGS.filter((t) => !form.tags.includes(t)).slice(0, 10).map((t) => (
                    <button key={t} onClick={() => setForm({ ...form, tags: [...form.tags, t] })} type="button">+ {t}</button>
                  ))}
                </div>
              </div>
              <div className="blog-editor-field">
                <label>
                  <input name="featured" type="checkbox" checked={form.featured} onChange={handleChange} />
                  {' '}Mark as Featured Article
                </label>
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════════════════
             CONTENT TAB — Visual Block Editor
             ════════════════════════════════════════════════════ */}
          {tab === 'content' && (
            <div className="blog-editor-content ve-content">
              {form.sections.length === 0 && (
                <div className="blog-editor-empty">
                  <FiZap size={36} />
                  <h3>Start building your article</h3>
                  <p>Add content blocks below — text, images, videos, quotes, code, and more.</p>
                </div>
              )}

              {form.sections.map((section, i) => (
                <div key={i} className={`ve-block ve-block--${section.type}`}>
                  {/* Block header */}
                  <div className="ve-block-header">
                    <span className="ve-block-type-badge">
                      {SECTION_TYPES.find((s) => s.type === section.type)?.icon}
                      {SECTION_TYPES.find((s) => s.type === section.type)?.label || section.type}
                    </span>
                    <div className="ve-block-actions">
                      <button onClick={() => moveSection(i, -1)} disabled={i === 0} title="Move up"><FiChevronUp /></button>
                      <button onClick={() => moveSection(i, 1)} disabled={i === form.sections.length - 1} title="Move down"><FiChevronDown /></button>
                      <button onClick={() => duplicateSection(i)} title="Duplicate"><FiPlus /></button>
                      <button onClick={() => deleteSection(i)} title="Delete" className="ve-delete-btn"><FiTrash2 /></button>
                    </div>
                  </div>

                  {/* ── TEXT SECTION ─────────────────────────── */}
                  {section.type === 'text' && (
                    <div className="ve-text-block">
                      <input value={section.title || ''} onChange={(e) => updateSection(i, { title: e.target.value })}
                        placeholder="Section heading (optional)..." className="ve-section-heading" />
                      <div className="ve-formatting-toolbar">
                        <button onClick={() => insertMarkdown(i, '**')} title="Bold"><FiBold /></button>
                        <button onClick={() => insertMarkdown(i, '*')} title="Italic"><FiItalic /></button>
                        <button onClick={() => insertMarkdown(i, '[', '](url)')} title="Link"><FiLink /></button>
                        <button onClick={() => insertMarkdown(i, '\n• ')} title="Bullet list"><FiList /></button>
                        <button onClick={() => insertMarkdown(i, '\n> ')} title="Inline quote"><FiMessageSquare /></button>
                        <span className="ve-toolbar-divider" />
                        <span className="ve-toolbar-hint">Markdown supported</span>
                      </div>
                      <textarea id={`section-body-${i}`} value={section.content || ''}
                        onChange={(e) => updateSection(i, { content: e.target.value })}
                        placeholder="Write your content here…&#10;&#10;Use **bold**, *italic*, [links](url), bullet lists (• item), and double line breaks for paragraphs."
                        rows={10} className="ve-text-body" />
                    </div>
                  )}

                  {/* ── IMAGE SECTION ────────────────────────── */}
                  {section.type === 'image' && (
                    <div className="ve-image-block">
                      <div className="ve-image-input-row">
                        <input value={section.url || ''} onChange={(e) => updateSection(i, { url: e.target.value })}
                          placeholder="Image URL or upload a file..." style={{ flex: 1 }} />
                        <label className="admin-btn secondary ve-upload-btn" style={{ cursor: 'pointer', margin: 0 }}>
                          {sectionUploading === i ? 'Uploading...' : <><FiUpload /> Upload</>}
                          <input type="file" accept="image/*" style={{ display: 'none' }}
                            onChange={(e) => handleSectionImageUpload(i, e)} disabled={sectionUploading === i} />
                        </label>
                      </div>
                      {section.url && (
                        <div className="ve-image-preview">
                          <img src={section.url} alt={section.alt || 'Uploaded image'} />
                        </div>
                      )}
                      <div className="ve-image-meta-row">
                        <input value={section.caption || ''} onChange={(e) => updateSection(i, { caption: e.target.value })}
                          placeholder="Caption (optional)..." className="ve-caption-input" />
                        <input value={section.alt || ''} onChange={(e) => updateSection(i, { alt: e.target.value })}
                          placeholder="Alt text for accessibility..." className="ve-alt-input" />
                      </div>
                    </div>
                  )}

                  {/* ── VIDEO SECTION ────────────────────────── */}
                  {section.type === 'video' && (
                    <div className="ve-video-block">
                      <div className="ve-video-input-row">
                        <FiVideo className="ve-input-icon" />
                        <input value={section.url || ''} onChange={(e) => updateSection(i, { url: e.target.value })}
                          placeholder="Paste YouTube or Vimeo URL..." style={{ flex: 1 }} />
                      </div>
                      {getVideoEmbedUrl(section.url) && (
                        <div className="ve-video-preview">
                          <iframe src={getVideoEmbedUrl(section.url)} title="Video preview"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen frameBorder="0" />
                        </div>
                      )}
                      {section.url && !getVideoEmbedUrl(section.url) && (
                        <div className="ve-video-invalid">
                          <FiAlertCircle /> Paste a valid YouTube or Vimeo URL
                        </div>
                      )}
                      <input value={section.caption || ''} onChange={(e) => updateSection(i, { caption: e.target.value })}
                        placeholder="Video caption (optional)..." className="ve-caption-input" />
                    </div>
                  )}

                  {/* ── QUOTE SECTION ────────────────────────── */}
                  {section.type === 'quote' && (
                    <div className="ve-quote-block">
                      <div className="ve-quote-mark">❝</div>
                      <textarea value={section.content || ''} onChange={(e) => updateSection(i, { content: e.target.value })}
                        placeholder="Enter the quote text..." rows={4} className="ve-quote-text" />
                      <div className="ve-quote-meta-row">
                        <input value={section.attribution || ''} onChange={(e) => updateSection(i, { attribution: e.target.value })}
                          placeholder="— Author / Speaker" className="ve-quote-author" />
                        <input value={section.source || ''} onChange={(e) => updateSection(i, { source: e.target.value })}
                          placeholder="Source / Publication (optional)" className="ve-quote-source" />
                      </div>
                    </div>
                  )}

                  {/* ── CODE SECTION ─────────────────────────── */}
                  {section.type === 'code' && (
                    <div className="ve-code-block">
                      <div className="ve-code-toolbar">
                        <select value={section.language || 'javascript'}
                          onChange={(e) => updateSection(i, { language: e.target.value })}>
                          {CODE_LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
                        </select>
                        <input value={section.caption || ''} onChange={(e) => updateSection(i, { caption: e.target.value })}
                          placeholder="Code caption / filename..." className="ve-code-caption" />
                      </div>
                      <textarea value={section.content || ''} onChange={(e) => updateSection(i, { content: e.target.value })}
                        placeholder="Paste your code here..." rows={10} className="ve-code-body"
                        spellCheck={false} />
                    </div>
                  )}

                  {/* ── CALLOUT SECTION ──────────────────────── */}
                  {section.type === 'callout' && (
                    <div className={`ve-callout-block ve-callout--${section.variant || 'info'}`}>
                      <div className="ve-callout-toolbar">
                        {CALLOUT_VARIANTS.map((v) => (
                          <button key={v.value}
                            className={`ve-callout-variant-btn ${(section.variant || 'info') === v.value ? 'active' : ''}`}
                            onClick={() => updateSection(i, { variant: v.value })}>
                            {v.icon} {v.label}
                          </button>
                        ))}
                      </div>
                      <input value={section.title || ''} onChange={(e) => updateSection(i, { title: e.target.value })}
                        placeholder="Callout heading (optional)..." className="ve-callout-title" />
                      <textarea value={section.content || ''} onChange={(e) => updateSection(i, { content: e.target.value })}
                        placeholder="Callout content..." rows={4} className="ve-callout-body" />
                    </div>
                  )}

                  {/* ── CITATION SECTION ─────────────────────── */}
                  {section.type === 'citation' && (
                    <div className="ve-citation-block">
                      <div className="ve-citation-icon"><FiBookOpen /></div>
                      <div className="ve-citation-fields">
                        <input value={section.title || ''} onChange={(e) => updateSection(i, { title: e.target.value })}
                          placeholder="Source title..." className="ve-cite-title" />
                        <div className="ve-cite-row">
                          <input value={section.author || ''} onChange={(e) => updateSection(i, { author: e.target.value })}
                            placeholder="Author(s)..." />
                          <input value={section.publication || ''} onChange={(e) => updateSection(i, { publication: e.target.value })}
                            placeholder="Publication / Journal..." />
                          <input value={section.date || ''} onChange={(e) => updateSection(i, { date: e.target.value })}
                            placeholder="Date..." type="text" />
                        </div>
                        <div className="ve-cite-row">
                          <input value={section.url || ''} onChange={(e) => updateSection(i, { url: e.target.value })}
                            placeholder="URL (optional)..." style={{ flex: 2 }} />
                        </div>
                        <textarea value={section.excerpt || ''} onChange={(e) => updateSection(i, { excerpt: e.target.value })}
                          placeholder="Key excerpt or summary from the source..." rows={3} className="ve-cite-excerpt" />
                      </div>
                    </div>
                  )}

                  {/* ── DIVIDER SECTION ──────────────────────── */}
                  {section.type === 'divider' && (
                    <div className="ve-divider-block">
                      <div className="ve-divider-options">
                        {['line', 'dots', 'stars', 'space'].map((s) => (
                          <button key={s}
                            className={`ve-divider-style-btn ${(section.style || 'line') === s ? 'active' : ''}`}
                            onClick={() => updateSection(i, { style: s })}>
                            {s === 'line' && '───'}{s === 'dots' && '• • •'}{s === 'stars' && '✦ ✦ ✦'}{s === 'space' && '⎵ Space'}
                          </button>
                        ))}
                      </div>
                      <div className={`ve-divider-preview ve-divider--${section.style || 'line'}`} />

                  {/* ── CHART SECTION ────────────────────────── */}
                  {section.type === 'chart' && (
                    <div className="ve-chart-block">
                      <div className="ve-chart-toolbar">
                        <select
                          value={section.chartConfig?.chartType || 'bar'}
                          onChange={(e) => updateSection(i, { chartConfig: { ...section.chartConfig, chartType: e.target.value } })}
                          className="ve-code-lang-select"
                        >
                          <option value="bar">Bar Chart</option>
                          <option value="horizontal-bar">Horizontal Bar</option>
                          <option value="line">Line Chart</option>
                          <option value="area">Area Chart</option>
                          <option value="pie">Pie / Donut</option>
                          <option value="grouped-bar">Grouped Bar</option>
                          <option value="stacked-bar">Stacked Bar</option>
                          <option value="scatter">Scatter Plot</option>
                          <option value="radar">Radar Chart</option>
                        </select>
                      </div>
                      <div className="chart-color-picker">
                        <label className="chart-color-label">Series Colors</label>
                        <div className="chart-color-swatches">
                          {(section.chartConfig?.colors || CHART_PALETTE).map((c, ci) => (
                            <div key={ci} className="chart-color-swatch-wrap">
                              <input
                                type="color"
                                value={c}
                                className="chart-color-input"
                                onChange={(e) => {
                                  const newColors = [...(section.chartConfig?.colors || CHART_PALETTE)];
                                  newColors[ci] = e.target.value;
                                  updateSection(i, { chartConfig: { ...section.chartConfig, colors: newColors } });
                                }}
                              />
                              <span className="chart-color-hex">{c}</span>
                            </div>
                          ))}
                          <button
                            className="chart-color-add"
                            onClick={() => {
                              const newColors = [...(section.chartConfig?.colors || CHART_PALETTE), '#64748b'];
                              updateSection(i, { chartConfig: { ...section.chartConfig, colors: newColors } });
                            }}
                            title="Add color"
                          >+</button>
                        </div>
                        <div className="chart-preset-palettes">
                          <span>Presets:</span>
                          {CHART_PRESETS.map((preset, pi) => (
                            <button key={pi} className="chart-preset-btn" title={preset.name}
                              onClick={() => updateSection(i, { chartConfig: { ...section.chartConfig, colors: [...preset.colors] } })}>
                              {preset.colors.slice(0, 5).map((clr, k) => (
                                <span key={k} className="chart-preset-dot" style={{ background: clr }} />
                              ))}
                            </button>
                          ))}
                        </div>
                      </div>
                      <textarea
                        value={section.chartConfig?.data ? JSON.stringify(section.chartConfig.data, null, 2) : '[]'}
                        onChange={(e) => {
                          try {
                            const parsed = JSON.parse(e.target.value);
                            updateSection(i, { chartConfig: { ...section.chartConfig, data: parsed } });
                          } catch { /* let user keep typing */ }
                        }}
                        placeholder='[{"name": "A", "value": 42}]'
                        rows={8}
                        className="ve-code-textarea"
                      />
                      <input value={section.title || ''} onChange={(e) => updateSection(i, { title: e.target.value })}
                        placeholder="Chart title..." className="ve-caption-input" />
                      <input value={section.caption || ''} onChange={(e) => updateSection(i, { caption: e.target.value })}
                        placeholder="Chart caption / description..." className="ve-caption-input" />
                    </div>
                  )}
                    </div>
                  )}
                </div>
              ))}

              {/* ── ADD BLOCK MENU ──────────────────────────── */}
              <div className="ve-add-block-area">
                {showBlockMenu ? (
                  <div className="ve-block-menu">
                    <div className="ve-block-menu-header">
                      <span>Add Content Block</span>
                      <button onClick={() => setShowBlockMenu(false)}>&times;</button>
                    </div>
                    <div className="ve-block-menu-grid">
                      {SECTION_TYPES.map((st) => (
                        <button key={st.type} className="ve-block-menu-item" onClick={() => addSection(st.type)}>
                          <span className="ve-block-menu-icon">{st.icon}</span>
                          <span className="ve-block-menu-label">{st.label}</span>
                          <span className="ve-block-menu-desc">{st.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <button className="ve-add-block-btn" onClick={() => setShowBlockMenu(true)}>
                    <FiPlus /> Add Block
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════════════════
             PREVIEW TAB — Full-fidelity rendering
             ════════════════════════════════════════════════════ */}
          {tab === 'preview' && (
            <div className="blog-editor-preview ve-preview">
              {form.coverImage && (
                <div className="blog-preview-cover">
                  <img src={form.coverImage} alt="Cover" />
                </div>
              )}
              <div className="blog-preview-meta">
                <span style={{ background: '#0052cc', color: '#fff', padding: '2px 10px', borderRadius: 12, fontSize: 12 }}>
                  {form.category}
                </span>
                <span>{form.author}</span>
                <span>{form.readTime} min read</span>
              </div>
              <h1 className="blog-preview-title">{form.title || 'Untitled Article'}</h1>
              <p className="blog-preview-excerpt">{form.excerpt}</p>

              {form.sections.map((s, i) => (
                <div key={i} className="ve-preview-block">
                  {/* TEXT */}
                  {s.type === 'text' && (
                    <div className="blog-preview-section">
                      {s.title && <h2>{s.title}</h2>}
                      {renderMarkdownContent(s.content)}
                    </div>
                  )}

                  {/* IMAGE */}
                  {s.type === 'image' && s.url && (
                    <figure className="ve-preview-image">
                      <img src={s.url} alt={s.alt || ''} />
                      {s.caption && <figcaption>{s.caption}</figcaption>}
                    </figure>
                  )}

                  {/* VIDEO */}
                  {s.type === 'video' && getVideoEmbedUrl(s.url) && (
                    <div className="ve-preview-video">
                      <iframe src={getVideoEmbedUrl(s.url)} title="Video" allowFullScreen frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
                      {s.caption && <p className="ve-preview-caption">{s.caption}</p>}
                    </div>
                  )}

                  {/* QUOTE */}
                  {s.type === 'quote' && (
                    <blockquote className="ve-preview-quote">
                      <p>{s.content}</p>
                      {(s.attribution || s.source) && (
                        <footer>
                          {s.attribution && <cite>— {s.attribution}</cite>}
                          {s.source && <span className="ve-quote-src">, {s.source}</span>}
                        </footer>
                      )}
                    </blockquote>
                  )}

                  {/* CODE */}
                  {s.type === 'code' && (
                    <div className="ve-preview-code">
                      {(s.caption || s.language) && (
                        <div className="ve-preview-code-header">
                          <span>{s.language}</span>
                          {s.caption && <span>{s.caption}</span>}
                        </div>
                      )}
                      <pre><code>{s.content}</code></pre>
                    </div>
                  )}

                  {/* CALLOUT */}
                  {s.type === 'callout' && (
                    <div className={`ve-preview-callout ve-preview-callout--${s.variant || 'info'}`}>
                      <span className="ve-preview-callout-icon">
                        {CALLOUT_VARIANTS.find((v) => v.value === (s.variant || 'info'))?.icon}
                      </span>
                      <div>
                        {s.title && <strong>{s.title}</strong>}
                        <p>{s.content}</p>
                      </div>
                    </div>
                  )}

                  {/* CITATION */}
                  {s.type === 'citation' && (
                    <div className="ve-preview-citation">
                      <FiBookOpen className="ve-preview-cite-icon" />
                      <div>
                        <strong>{s.title}</strong>
                        <div className="ve-preview-cite-meta">
                          {s.author && <span>{s.author}</span>}
                          {s.publication && <span>{s.publication}</span>}
                          {s.date && <span>{s.date}</span>}
                        </div>
                        {s.excerpt && <p className="ve-preview-cite-excerpt">{s.excerpt}</p>}
                        {s.url && <a href={s.url} target="_blank" rel="noopener noreferrer"><FiExternalLink /> View Source</a>}
                      </div>
                    </div>
                  )}

                  {/* DIVIDER */}
                  {s.type === 'divider' && (
                    <div className={`ve-preview-divider ve-preview-divider--${s.style || 'line'}`} />
                  )}
                </div>
              ))}

              {form.tags.length > 0 && (
                <div className="blog-preview-tags">
                  {form.tags.map((t) => <span key={t}>#{t}</span>)}
                </div>
              )}
            </div>
          )}

        </div>{/* end editor-card */}
      </div>{/* end admin-body */}
    </div>{/* end admin-page */}
    </GoogleSignInGate>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MARKDOWN-LITE RENDERER for preview
   ═══════════════════════════════════════════════════════════════ */
function renderMarkdownContent(content) {
  if (!content) return null;
  return content.split('\n\n').map((para, j) => {
    // Bullet lists
    if (para.startsWith('•') || para.startsWith('- ')) {
      const items = para.split('\n').filter(Boolean);
      return (
        <ul key={j} className="blog-section-list">
          {items.map((item, k) => <li key={k}>{renderInline(item.replace(/^[•-]\s*/, ''))}</li>)}
        </ul>
      );
    }
    // Numbered lists
    if (/^\d+\./.test(para)) {
      const items = para.split('\n').filter(Boolean);
      return (
        <ol key={j} className="blog-section-list">
          {items.map((item, k) => <li key={k}>{renderInline(item.replace(/^\d+\.\s*/, ''))}</li>)}
        </ol>
      );
    }
    // Block quotes (> prefixed)
    if (para.startsWith('> ')) {
      return <blockquote key={j} className="ve-inline-quote">{renderInline(para.replace(/^>\s*/gm, ''))}</blockquote>;
    }
    return <p key={j}>{renderInline(para)}</p>;
  });
}

/* Inline markdown: **bold**, *italic*, [text](url) */
function renderInline(text) {
  if (!text) return text;
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Bold
    let m = remaining.match(/\*\*(.+?)\*\*/);
    if (m && m.index !== undefined) {
      if (m.index > 0) parts.push(remaining.substring(0, m.index));
      parts.push(<strong key={key++}>{m[1]}</strong>);
      remaining = remaining.substring(m.index + m[0].length);
      continue;
    }
    // Italic
    m = remaining.match(/\*(.+?)\*/);
    if (m && m.index !== undefined) {
      if (m.index > 0) parts.push(remaining.substring(0, m.index));
      parts.push(<em key={key++}>{m[1]}</em>);
      remaining = remaining.substring(m.index + m[0].length);
      continue;
    }
    // Link
    m = remaining.match(/\[(.+?)\]\((.+?)\)/);
    if (m && m.index !== undefined) {
      if (m.index > 0) parts.push(remaining.substring(0, m.index));
      parts.push(<a key={key++} href={m[2]} target="_blank" rel="noopener noreferrer">{m[1]}</a>);
      remaining = remaining.substring(m.index + m[0].length);
      continue;
    }
    // No more matches
    parts.push(remaining);
    break;
  }
  return parts;
}
